/**
 * The five questions -- the product's top-level navigation
 * (docs/the-story.md, section 3; navigation re-cut, option A).
 *
 * A person does not arrive with a module in mind; they arrive with a
 * question. Each question owns the destinations that answer it, drawn
 * from all three modules. This file is the ONE place that says which
 * question a page belongs to: the masthead, the second row and the
 * breadcrumb all read it, so they cannot disagree.
 *
 * A destination a seat may not open is left out of that seat's row
 * (E1's rule: do not offer rooms that will refuse you), and a question
 * with nothing left for a seat is left out of the masthead.
 */
import type { LicenseStatus, MeOut } from "shared/api/types";
import {
  canReadBoard,
  canReadCeremony,
  canRunOperations,
  hasModule,
} from "shared/api/authority";
import { hasPosition } from "shared/api/useMe";
import { canSeeExecutive } from "shared/executive/ExecutiveHome";
import type { MarkName } from "shared/icons/marks.generated";
import { isModuleLicensed } from "shared/licensing/useLicense";

export type QuestionKey = "matters" | "safe" | "owe" | "do" | "prove";

export interface Destination {
  to: string;
  labelKey: string;
  icon: MarkName;
  /** The feature module the page lives in, for the licence and the seat. */
  module?: "foundation" | "twin" | "framework";
  /** Who may open it, beyond the module. Absent: anyone with the module. */
  allow?: (me: MeOut | undefined) => boolean;
}

export interface Question {
  key: QuestionKey;
  /** The short masthead label ("Safe right now"). */
  navKey: string;
  /** The question in full ("Is it safe right now?"), for the row and crumb. */
  questionKey: string;
  destinations: readonly Destination[];
  /** Pages that belong to the question without a place in its row:
   *  an entity or record page reached from one of its destinations. */
  alsoUnder?: readonly string[];
}

const approverish = (me: MeOut | undefined) =>
  (me?.is_admin ?? false) || hasPosition(me, "approver");
const readinessish = (me: MeOut | undefined) =>
  canReadBoard(me) || (me?.realm_roles ?? []).includes("head_of_opsres");
const fieldExamish = (me: MeOut | undefined) =>
  approverish(me) || hasPosition(me, "reviewer") || canRunOperations(me);

export const QUESTIONS: readonly Question[] = [
  {
    key: "matters",
    navKey: "nav.question.matters",
    questionKey: "nav.question.mattersFull",
    destinations: [
      {
        to: "/twin/ibs-portfolio",
        labelKey: "nav.tab.twin__ibs-portfolio",
        icon: "ibs",
        module: "twin",
      },
      {
        to: "/twin/ibs-groups",
        labelKey: "nav.tab.twin__ibs-groups",
        icon: "ibs",
        module: "twin",
      },
      {
        to: "/twin/cifs",
        labelKey: "nav.tab.twin__cifs",
        icon: "ibs",
        module: "twin",
        // Round 17 Q2 -- the page refuses any other seat.
        allow: canReadCeremony,
      },
      {
        to: "/twin/ibs-admission",
        labelKey: "nav.tab.twin__ibs-admission",
        icon: "dual-signoff",
        module: "twin",
        // Round 17 Q2 -- the page refuses any other seat.
        allow: canReadCeremony,
      },
      {
        to: "/twin/mvb-candidates",
        labelKey: "nav.tab.twin__mvb-candidates",
        icon: "mvb",
        module: "twin",
        // Round 17 Q2 -- the page refuses any other seat.
        allow: canReadCeremony,
      },
      {
        to: "/foundation/mvb",
        labelKey: "nav.tab.foundation__mvb",
        icon: "mvb",
        module: "foundation",
        // Round 17 Q2 -- the page refuses any other seat.
        allow: canReadCeremony,
      },
      // Round 17 Q1 -- the treasurer's integrity tolerance and its
      // "Record an observation" were under no question.
      {
        to: "/twin/tolerance-metrics",
        labelKey: "nav.question.tolerances",
        icon: "tolerance-clock",
        module: "twin",
      },
      {
        to: "/foundation/admin/ibs-groups",
        labelKey: "nav.question.groupEditor",
        icon: "ibs",
        module: "foundation",
        allow: canRunOperations,
      },
      {
        to: "/twin/capmap",
        labelKey: "nav.tab.twin__capmap",
        icon: "capability-map",
        module: "twin",
      },
      {
        to: "/foundation/admin/services",
        labelKey: "nav.tab.foundation__admin__services",
        icon: "constellation",
        module: "foundation",
        // Round 17 Q2 -- the page refuses any other seat.
        allow: canRunOperations,
      },
      {
        to: "/foundation/admin/capabilities",
        labelKey: "nav.tab.foundation__admin__capabilities",
        icon: "capability-map",
        module: "foundation",
        // Round 17 Q2 -- the page refuses any other seat.
        allow: canRunOperations,
      },
      {
        to: "/foundation/admin/risk-appetite-statements",
        labelKey: "nav.tab.foundation__admin__risk-appetite-statements",
        icon: "tolerance-clock",
        module: "foundation",
        // Round 17 Q2 -- the page refuses any other seat.
        allow: canReadCeremony,
      },
    ],
    alsoUnder: [
      "/twin/ibs",
      "/twin/service",
      "/twin/capability",
      "/twin/vendor",
      "/twin/ci",
    ],
  },
  {
    key: "safe",
    navKey: "nav.question.safe",
    questionKey: "nav.question.safeFull",
    destinations: [
      {
        to: "/twin/cascade",
        labelKey: "nav.tab.twin__cascade",
        icon: "cascade",
        module: "twin",
      },
      // Round 20 U9 -- what is open now, newest first, beside the
      // cascade, which is ordered by entity (the question still lands
      // on the cascade).
      {
        to: "/twin/findings",
        labelKey: "nav.question.openFindings",
        icon: "finding",
        module: "twin",
      },
      {
        to: "/twin/war-room",
        labelKey: "nav.tab.twin__war-room",
        icon: "war-room",
        module: "twin",
      },
      {
        to: "/twin/scenarios",
        labelKey: "nav.tab.twin__scenarios",
        icon: "scenario",
        module: "twin",
      },
      {
        to: "/twin/telemetry",
        labelKey: "nav.tab.twin__telemetry",
        icon: "telemetry",
        module: "twin",
      },
      {
        to: "/twin/constellation",
        labelKey: "nav.tab.twin__constellation",
        icon: "constellation",
        module: "twin",
      },
      {
        to: "/twin/fragility",
        labelKey: "nav.tab.twin__fragility",
        icon: "fragility-radar",
        module: "twin",
      },
      {
        to: "/twin/continuity-posture",
        labelKey: "nav.question.continuity",
        icon: "tolerance-clock",
        module: "twin",
      },
      // Round 17 Q1 -- typed by the Resilience Lead, or reachable only
      // from a home card that goes once an incident exists.
      {
        to: "/twin/continuity-plans",
        labelKey: "nav.question.continuityPlans",
        icon: "drill",
        module: "twin",
      },
      // P421 -- the change register.
      {
        to: "/twin/changes",
        labelKey: "nav.tab.twin__changes",
        icon: "journey",
        module: "twin",
      },
      {
        to: "/twin/findings/new",
        labelKey: "nav.question.raiseFinding",
        icon: "finding",
        module: "twin",
        allow: canRunOperations,
      },
      {
        to: "/admin/edge-delays",
        labelKey: "nav.question.edgeDelays",
        icon: "cascade",
        allow: canRunOperations,
      },
    ],
    alsoUnder: ["/twin/findings"],
  },
  {
    key: "owe",
    navKey: "nav.question.owe",
    questionKey: "nav.question.oweFull",
    destinations: [
      {
        to: "/framework/assessments",
        labelKey: "nav.tab.framework__assessments",
        icon: "framework",
        module: "framework",
      },
      {
        to: "/framework/letters",
        labelKey: "nav.tab.framework__letters",
        icon: "obligation",
        module: "framework",
      },
      {
        to: "/framework/regulatory-change",
        labelKey: "nav.question.regulatoryChange",
        icon: "obligation",
        module: "framework",
      },
      {
        to: "/framework/document-gap",
        labelKey: "nav.question.documentGap",
        icon: "document-locker",
        module: "framework",
      },
      // Round 18 Q4 -- the locker sat under no question; it was reached
      // through a seeded document's name on the gap page.
      {
        to: "/admin/documents",
        labelKey: "nav.question.documentLocker",
        icon: "document-locker",
        allow: (me) => (me?.is_admin ?? false) || hasPosition(me, "preparer"),
      },
      {
        to: "/framework/attestations",
        labelKey: "nav.question.attestations",
        icon: "attestation",
        module: "framework",
      },
      {
        to: "/twin/field-exam",
        labelKey: "nav.tab.twin__field-exam",
        icon: "obligation",
        module: "twin",
        allow: fieldExamish,
      },
      {
        to: "/recommendations",
        labelKey: "nav.question.recommendations",
        icon: "recommendation",
      },
    ],
    alsoUnder: ["/framework/caps"],
  },
  {
    key: "do",
    navKey: "nav.question.do",
    questionKey: "nav.question.doFull",
    destinations: [
      { to: "/my-work", labelKey: "nav.question.myWork", icon: "dual-signoff" },
      {
        to: "/framework/queue/preparer",
        labelKey: "nav.tab.framework__queue__preparer",
        icon: "dual-signoff",
        module: "framework",
      },
      {
        to: "/framework/queue/reviewer",
        labelKey: "nav.tab.framework__queue__reviewer",
        icon: "dual-signoff",
        module: "framework",
      },
      {
        to: "/framework/queue/approver",
        labelKey: "nav.tab.framework__queue__approver",
        icon: "dual-signoff",
        module: "framework",
      },
    ],
  },
  {
    key: "prove",
    navKey: "nav.question.prove",
    questionKey: "nav.question.proveFull",
    destinations: [
      {
        to: "/executive",
        labelKey: "nav.question.overview",
        icon: "dossier",
        allow: canSeeExecutive,
      },
      {
        to: "/board",
        labelKey: "nav.question.board",
        icon: "dossier",
        allow: canReadBoard,
      },
      {
        to: "/assurance",
        labelKey: "nav.question.assurance",
        icon: "verifier",
        allow: approverish,
      },
      {
        to: "/readiness",
        labelKey: "nav.question.readiness",
        icon: "benchmark",
        allow: readinessish,
      },
      {
        to: "/benchmark",
        labelKey: "nav.question.benchmark",
        icon: "benchmark",
        allow: approverish,
      },
      {
        to: "/twin/test-runs",
        labelKey: "nav.tab.twin__test-runs",
        icon: "scenario",
        module: "twin",
      },
      {
        to: "/twin/drills",
        labelKey: "nav.tab.twin__drills",
        icon: "drill",
        module: "twin",
      },
      // Round 18 Q4 -- the MVB validation drill was under no question.
      {
        to: "/twin/mvb-drill",
        labelKey: "nav.question.mvbDrill",
        icon: "drill",
        module: "twin",
      },
      // Round 17 Q1 -- the examiner's seeded report sat in a register
      // no question listed.
      {
        to: "/twin/incident-reports",
        labelKey: "nav.tab.twin__incident-reports",
        icon: "finding",
        module: "twin",
      },
    ],
  },
];

function under(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to + "/");
}

/** The question a page belongs to, by longest-prefix match over every
 *  destination and `alsoUnder` path; null for a page no question claims
 *  (Admin, a module's own home, a register kept for administration). */
export function questionFor(pathname: string): QuestionKey | null {
  let best: { key: QuestionKey; len: number } | null = null;
  for (const q of QUESTIONS) {
    const paths = [...q.destinations.map((d) => d.to), ...(q.alsoUnder ?? [])];
    for (const to of paths) {
      if (under(pathname, to) && (best === null || to.length > best.len)) {
        best = { key: q.key, len: to.length };
      }
    }
  }
  return best?.key ?? null;
}

/** The destinations of a question this seat may open. */
export function visibleDestinations(
  q: Question,
  me: MeOut | undefined,
  lic: LicenseStatus | undefined,
): Destination[] {
  return q.destinations.filter(
    (d) =>
      (!d.module || (isModuleLicensed(lic, d.module) && hasModule(me, d.module))) &&
      (!d.allow || d.allow(me)),
  );
}
